import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import './Booking.css';

export default function BookAppointment() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    serviceId: '', appointmentType: 'video', date: '', time: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    apiClient.get('/services')
      .then((res) => setServices(res.data.services))
      .catch(() => setError('Could not load services. Please refresh, or contact support if this persists.'));
  }, []);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!user) {
      navigate('/login', { state: { redirectTo: '/book' } });
      return;
    }

    if (!form.date || !form.time) {
      setError('Please choose a date and time.');
      return;
    }

    const scheduledAt = new Date(`${form.date}T${form.time}:00`).toISOString();

    setBusy(true);
    try {
      const res = await apiClient.post('/appointments', {
        serviceId: form.serviceId || null,
        appointmentType: form.appointmentType,
        scheduledAt,
        patientTimezone: timezone,
      });
      setSuccess('Appointment requested! You can now start your AI-assisted intake before your session.');
      setTimeout(() => navigate(`/intake/${res.data.appointment.id}`), 1400);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not book appointment');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="booking-page">
      <div className="container booking-inner">
        <div>
          <span className="eyebrow">Book an Appointment</span>
          <h1>Let's find a time that works for you</h1>
          <p className="section-intro">
            Times are shown in your local timezone ({timezone}). After booking, you'll complete
            a short AI-assisted intake so your clinician can prepare before your session.
          </p>
        </div>

        <form className="card booking-form" onSubmit={handleSubmit}>
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <div className="form-field">
            <label htmlFor="service">Service</label>
            <select id="service" value={form.serviceId} onChange={(e) => update('serviceId', e.target.value)}>
              <option value="">{services.length ? 'Select a service' : 'Loading services…'}</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>{s.name} — {s.currency} {s.price}</option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="appointmentType">Appointment type</label>
            <select id="appointmentType" value={form.appointmentType} onChange={(e) => update('appointmentType', e.target.value)}>
              <option value="video">Video call</option>
              <option value="audio">Audio call</option>
              <option value="chat">Secure chat</option>
              <option value="in_person">In-person (Eldoret office)</option>
            </select>
          </div>

          <div className="booking-row">
            <div className="form-field">
              <label htmlFor="date">Date</label>
              <input id="date" type="date" required value={form.date} onChange={(e) => update('date', e.target.value)} />
            </div>
            <div className="form-field">
              <label htmlFor="time">Time</label>
              <input id="time" type="time" required value={form.time} onChange={(e) => update('time', e.target.value)} />
            </div>
          </div>

          <button className="btn btn-primary" disabled={busy}>
            {busy ? 'Booking…' : user ? 'Request Appointment' : 'Log in to Book'}
          </button>
        </form>
      </div>
    </div>
  );
}
